// src/pages/CMS/Admin/PasswordChangeModal.jsx
import React, { useState } from "react";
import { changePassword } from "../../../../utils/cmsAuth";

/**
 * props:
 * - open: boolean
 * - username: string
 * - roleLabel: string ("최고 관리자" 등)
 * - onDone: () => void   // 저장 성공 또는 닫기 콜백 (강제 모드면 저장 성공시에만 호출하는 쪽 권장)
 * - force?: boolean      // true면 취소/외부 클릭으로 닫히지 않음 (기본 false)
 */
export default function PasswordChangeModal({ open, username, roleLabel, onDone, force = false }) {
  const [oldPw, setOld] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const validate = () => {
    if (!oldPw || !pw || !pw2) return "모든 항목을 입력해주세요.";
    if (pw !== pw2) return "새 비밀번호가 일치하지 않습니다.";
    if (pw.length < 8) return "비밀번호는 8자 이상이어야 합니다.";
    // (선택) 복잡도 예시: 영문/숫자/특수문자 2가지 이상
    // const groups = [/[a-zA-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((r) => r.test(pw)).length;
    // if (groups < 2) return "영문/숫자/특수문자 중 2가지 이상을 포함해주세요.";
    return "";
  };

  const save = async () => {
    if (saving) return;
    const msg = validate();
    if (msg) return setErr(msg);
    setErr("");
    setSaving(true);
    try {
      await changePassword(oldPw, pw);
      // 입력값 리셋
      setOld(""); setPw(""); setPw2("");
      onDone?.();
    } catch (e) {
      setErr(e?.message || "변경에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    save();
  };

  const onBackdropClick = (e) => {
    if (force) return; // 강제 모드에서는 닫기 금지
    // backdrop 클릭 시 닫기
    if (e.target === e.currentTarget) onDone?.();
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center"
      onMouseDown={onBackdropClick}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative w-[520px] rounded-2xl bg-white p-6 shadow-xl"
        onMouseDown={(e) => e.stopPropagation()} // 내부 클릭은 전파 막기
        role="dialog"
        aria-modal="true"
        aria-labelledby="pwd-change-title"
      >
        <div id="pwd-change-title" className="text-base font-semibold mb-4">
          비밀번호 변경
        </div>

        {/* 사용자 정보 */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="text-sm text-neutral-500">운영자 ID</div>
          <div className="text-sm font-medium">{username}</div>
          <div className="text-sm text-neutral-500">권한</div>
          <div className="text-sm font-medium">{roleLabel}</div>
        </div>

        <form className="space-y-3" onSubmit={onSubmit}>
          <div>
            <label htmlFor="oldPw" className="sr-only">현재 비밀번호</label>
            <input
              id="oldPw"
              type="password"
              placeholder="현재 비밀번호를 입력해주세요."
              className="w-full rounded-md border border-neutral-200 px-3 py-2 outline-none focus:border-[#7864F9]"
              value={oldPw}
              onChange={(e) => { setOld(e.target.value); if (err) setErr(""); }}
              autoComplete="current-password"
            />
          </div>
          <div>
            <label htmlFor="newPw" className="sr-only">새 비밀번호</label>
            <input
              id="newPw"
              type="password"
              placeholder="새로운 비밀번호를 입력해주세요."
              className="w-full rounded-md border border-neutral-200 px-3 py-2 outline-none focus:border-[#7864F9]"
              value={pw}
              onChange={(e) => { setPw(e.target.value); if (err) setErr(""); }}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label htmlFor="newPw2" className="sr-only">새 비밀번호 확인</label>
            <input
              id="newPw2"
              type="password"
              placeholder="비밀번호를 확인해주세요."
              className="w-full rounded-md border border-neutral-200 px-3 py-2 outline-none focus:border-[#7864F9]"
              value={pw2}
              onChange={(e) => { setPw2(e.target.value); if (err) setErr(""); }}
              autoComplete="new-password"
            />
          </div>

          {/* 가이드/에러 */}
          <div className="text-xs text-neutral-500">
            비밀번호는 8자 이상을 권장합니다.
          </div>
          {err && <div className="text-sm text-red-500">{err}</div>}

          <div className="mt-4 flex justify-end gap-2">
            {!force && (
              <button
                type="button"
                className="px-4 py-2 rounded-md border border-neutral-200"
                disabled={saving}
                onClick={onDone}
              >
                취소
              </button>
            )}
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-[#7864F9] text-white font-semibold disabled:opacity-60"
              disabled={saving}
            >
              {saving ? "저장 중..." : "저장"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
